import { FC, useEffect, useCallback, useState } from "react";
import {
  ConfirmModal,
  ExpandedTable,
  FileField,
  ICheckbox,
  SearchFormField,
  StoredPagination,
} from "../../../ui-kit";
import { IconButton } from "../../../ui-kit";
import { Layout } from "../../../ui-kit/Layout/Layout";
import { Col, Row } from "antd";
import { CheckOutlined } from "@ant-design/icons";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import ExpandedContent from "./ExpandedContent";
import nsiOrgStore from "../../../store/nsiOrganizationsStore";
import { debounce } from "lodash";
import { ISelect } from "../../../ui-kit/ISelect/ISelect";
import { Controller } from "react-hook-form";
import Paths from "../../../constants/PathsBreadcrumbs";
import { useBreadcrumbs } from "../../../hooks/useBreadcrumbs";
import { useHandleForm } from "../../../hooks/useHandleForm";
import { useStatusMessage } from "../../../hooks/useStatusMessage";
import OrganizationForm from "./OrganizationForm/OrganizationForm";
import { emptyOrganization } from "./initData";
import styles from "./NsiOrganizations.module.scss";
import HistoryModal from "../../../ui-kit/HistoryModal/HistoryModal";

const NsiOrganizations: FC = observer(() => {
  useBreadcrumbs(Paths.nsiOrganizations);

  const [searchText, setSearchText] = useState("");
  const [filterGrbs, setFilterGrbs] = useState(0);
  const [filterIsRso, setFilterRso] = useState(false);
  const [filterIsGrbs, setFilterIsGrbs] = useState(false);

  const [openHistory, setOpenHistory] = useState(false);
  const [orgHistory, setOrgHistory] = useState({ id: 0, name: "" });
  const { control } = useHandleForm();
  const [openNewOrgForm, setOpenNewOrgForm] = useState(false);
  const [deleteIdOrganization, setDeleteIdOrganization] = useState<
    number | null
  >(null);
  const {
    filter,
    firstOrgNumber,
    organizationRowKeys,
    messageStatus,
    organizations,
    organizationsLoading,
    paginator,
    search,
    sorter,
    typesRGBS,
  } = nsiOrgStore;

  const ruNames = {
    Name: "Название",
    ParentId: "ГРБС",
    Inn: "ИНН",
    isGrbs: "Признак ГРБС",
    isRso: "Приpнак РСО",
  };

  const tableCols = [
    {
      title: "№",
      render: (text, res, idx) => firstOrgNumber + idx,
    },
    {
      title: "Наименование организации",
      dataIndex: "name",
      render: (res) => (res ? res : "-"),
      className: "align-left",
      sortable: true,
      width: "25%",
    },
    {
      title: "ГРБС",
      dataIndex: "parent.Name",
      sortable: true,
      className: "align-left",
      width: "20%",
      render: (_, record) => record.grbsName,
    },
    {
      title: "ИНН",
      dataIndex: "inn",
      sortable: true,
    },
    {
      title: "Количество зданий",
      render: (_, record) => record.buildings.length,
    },
    {
      title: "Признак ГРБС",
      dataIndex: "isGrbs",
      sortable: true,
      render: (_, record) => {
        if (record.isGrbs) {
          return <CheckOutlined />;
        }
      },
    },
    {
      title: "Признак РСО",
      dataIndex: "isRso",
      sortable: true,
      render: (_, record) => {
        if (record.isRso) {
          return <CheckOutlined />;
        }
      },
    },
    {
      title: "Тип ресурса",
      render: (_, record) => {
        if (record.isRso) {
          return record.gridTypeNames?.map((gridName) => (
            <p key={gridName}>{gridName}</p>
          ));
        }
      },
    },
    {
      title: "Тип ГРБС",
      render: (_, record) => {
        return (
          <p key={record.grbsTypeId}>
            {typesRGBS.find((item) => item.id === record.grbsTypeId)?.title}
          </p>
        );
      },
    },
    {
      title: "",
      render: (_, record) => (
        <Row gutter={10} wrap={false}>
          <Col>
            <IconButton
              typeIcon={"edit"}
              variant="text"
              className={styles.actionButton}
              onClick={(e) => {
                record.showEdit = true;
              }}
            />
          </Col>
          {/* <Col>
            <IconButton
              typeIcon={"history"}
              variant="text"
              className={styles.actionButton}
              onClick={(e) => {
                e.stopPropagation();
                setOpenHistory(true);
                setOrgHistory(record);
              }}
            />
          </Col> */}
          <Col>
            <IconButton
              typeIcon={"delete"}
              variant="text"
              className={styles.actionButton}
              onClick={(e) => {
                e.stopPropagation();
                setDeleteIdOrganization(record.id);
              }}
            />
          </Col>
        </Row>
      ),
    },
  ];

  useStatusMessage(messageStatus);

  const onExpand = (expanded, record) => {
    if (!expanded) {
      record.showEdit = false;
    }
  };

  const debouncedSearchInput = useCallback(
    debounce((inputText) => (search.text = inputText), 500),
    [],
  );

  const handleClearSearch = () => {
    search.text = "";
    setSearchText("");
  };

  const handleChangeSearch = (event) => {
    setSearchText(event.target.value);
    debouncedSearchInput(event.target.value);
  };

  const handleSetFilterGrbs = (value) => {
    filter.setFilterItem("parentId", value);
    setFilterGrbs(value);
  };

  const handleChangeFilterRso = (_) => {
    if (filterIsRso) {
      filter.setFilterItem("isRso", "");
    } else {
      filter.setFilterItem("isRso", true);
    }
    setFilterRso(!filterIsRso);
  };

  const handleChangeFilterIsGrbs = (_) => {
    if (filterIsGrbs) {
      filter.setFilterItem("isGrbs", "");
    } else {
      filter.setFilterItem("isGrbs", true);
    }
    setFilterIsGrbs(!filterIsGrbs);
  };

  useEffect(() => {
    nsiOrgStore.fetchOrganizations();
  }, [
    search.text,
    paginator.page,
    paginator.perPage,
    filterGrbs,
    filterIsRso,
    filterIsGrbs,
    sorter.changingKey,
  ]);

  useEffect(() => {
    runInAction(() => {
      nsiOrgStore.getGrbsOrgs();
      nsiOrgStore.getOwnershipTypes();
      nsiOrgStore.getFtg();
      nsiOrgStore.getGridTypes();
      nsiOrgStore.getTypeRGBS();
    });
  }, []);
  const onFillProfileFromFile = (files) => {
    const formData = new FormData();
    formData.append("file", files[0]);
    nsiOrgStore.updatefromfile(formData);
  };

  return (
    <Layout type="white">
      <>
        <Row
          align="middle"
          gutter={[20, 20]}
          style={{ marginBottom: "50px" }}
          justify="start"
        >
          <Col xxl={9} sm={12}>
            <SearchFormField
              name="searchText"
              label="Поиск организации (название, ИНН)"
              value={searchText}
              onClear={handleClearSearch}
              onChange={handleChangeSearch}
              type="text"
              onFindClick={() => nsiOrgStore.fetchOrganizations()}
            />
          </Col>
          <Col xxl={9} span={7}>
            <Controller
              control={control}
              name="grbsName"
              render={({ field }) => (
                <ISelect
                  field={field}
                  optionsArr={nsiOrgStore.grbsArray}
                  placeholder="Фильтр по ГРБС"
                  onChange={handleSetFilterGrbs}
                />
              )}
            />
          </Col>
          <Col span={1}>
            <ICheckbox
              checked={filterIsRso}
              onChange={handleChangeFilterRso}
              label={"РСО"}
              className={styles.checkbox}
            />
          </Col>
          <Col span={1}>
            <ICheckbox
              checked={filterIsGrbs}
              onChange={handleChangeFilterIsGrbs}
              label={"ГРБС"}
              className={styles.checkbox}
            />
          </Col>

          <Col xxl={5} span={1}>
            <IconButton
              onClick={() => setOpenNewOrgForm(!openNewOrgForm)}
              typeIcon="add"
            >
              Добавить организацию
            </IconButton>
          </Col>
          <Col xxl={5} span={1}>
            <FileField
              onChange={onFillProfileFromFile}
              label="Загрузить файл"
            />
          </Col>
        </Row>
        <HistoryModal
          open={openHistory}
          onCancel={() => setOpenHistory(false)}
          title={orgHistory.name}
          historyUrl={`Orgstructure/OrganizationMain/${orgHistory.id}/History`}
          ruNames={ruNames}
          mask={true}
          scroll={{
            y: 540,
          }}
        />
        {openNewOrgForm && (
          <OrganizationForm
            organization={emptyOrganization}
            closeForm={() => setOpenNewOrgForm(!openNewOrgForm)}
          />
        )}
        <ExpandedTable
          columns={tableCols}
          data={organizations}
          rowKey={(record) => record.id}
          onExpand={onExpand}
          rowKeys={organizationRowKeys.keys}
          setRowKeys={organizationRowKeys.setKeys}
          className={styles.organisationsTable}
          sort={sorter}
          loading={organizationsLoading}
        >
          <ExpandedContent />
        </ExpandedTable>
        <StoredPagination {...paginator} />
      </>
      <ConfirmModal
        title="Удаление организации"
        message="Вы действительно хотите удалить организацию?"
        visible={!!deleteIdOrganization}
        onClose={() => setDeleteIdOrganization(null)}
        onConfirm={() => {
          nsiOrgStore.deleteOrganization(deleteIdOrganization);
          setDeleteIdOrganization(null);
        }}
      />
    </Layout>
  );
});

export default NsiOrganizations;
